import { useSelector } from "react-redux";
import styled from "styled-components";
import intl from "../../services/intl";
import Cart from "../../store/cart/model";
import { PromotionProgress } from "../../store/promotion-progress";
import { getPromotionProgressById } from "../../store/promotion-progress/selectors";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const Heading = styled.h2`
  margin: var(--spacing-0_5) 0 var(--spacing-0_5) 0;
  width: 100%;
  text-align: left;
`;

/**
 * This shows a partial progress bar with a yellow foreground, and a grey background,
 * with a white gap in the middle that is concave on the left and convex on the right.
 */
const PartialProgressBar = styled.div<{ $percent: number }>`
  width: 100%;
  height: 4px;
  background: linear-gradient(
    to right,
    var(--white) 0%,
    var(--white) calc(${(props) => props.$percent}% + 4px),
    var(--cool-40) calc(${(props) => props.$percent}% + 4px),
    var(--cool-40) 100%
  );
  border-radius: 10px;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: calc(${(props) => props.$percent}% + 4px);
    height: 4px;
    background-color: var(--yellow-ochre);
    border-radius: 10px;
    border-color: var(--white);
  }

  &::after {
    content: "";
    position: absolute;
    left: calc(${(props) => props.$percent}% + 4px);
    top: -1px;
    width: 4px;
    height: 6px;
    background-color: var(--white);
    border-radius: 0 4px 4px 0;
  }
`;

const FullProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background-color: var(--yellow-ochre);
  border-radius: 10px;
`;

const ProgressIndicator = ({
  cart,
  className,
}: {
  cart: Cart;
  className?: string;
}) => {
  const promotionProgress = useSelector(
    (state) =>
      getPromotionProgressById(
        state,
        cart.promotionProgressId,
      ) as PromotionProgress,
  );
  if (!promotionProgress) {
    return null;
  }

  const { criteriaType, currentValue, requiredValue, eligible } =
    promotionProgress;
  if (!currentValue || !requiredValue || !criteriaType) {
    return null;
  }
  const percent = Math.min((currentValue / requiredValue) * 100, 100);
  if (percent === 0) return null;

  const complete = percent >= 100 && eligible;

  const CurrentProgressBar = complete ? FullProgressBar : PartialProgressBar;

  return (
    <Container className={className}>
      <Heading
        className="typography-label2 font-circular"
        id="progress-indicator-progress-label"
      >
        {complete
          ? intl.t(
              `cart.promotion-progress.${criteriaType}.complete`,
              "You've unlocked a free gift 🎉",
            )
          : intl.t(
              `cart.promotion-progress.${criteriaType}.incomplete`,
              "Add another subscription product to get a free gift",
              {
                amount: intl.formatCurrency(
                  (requiredValue - currentValue) / 100,
                  { round: true },
                ),
              },
            )}
      </Heading>
      <CurrentProgressBar
        $percent={percent}
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        role="progressbar"
        aria-describedby="progress-indicator-progress-label"
      />
    </Container>
  );
};

export default ProgressIndicator;
